import {
  Button,
  Icon,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  cn,
} from "@nexus/ui";
import type { KeyboardEvent } from "react";
import { useId } from "react";
import { HiddenByFilterNote } from "@/features/marketplace/components/HiddenByFilterNote";
import { useTranslation } from "react-i18next";

import { DeadlineChip } from "@/features/marketplace/components/orders-list/DeadlineChip";
import { OrderScriptReferenceVideoLink } from "@/features/marketplace/components/orders-list/OrderScriptReferenceVideo";
import { OrderTypeBadge } from "@/features/marketplace/components/orders-list/OrderTypeBadge";
import { VideoStatusSummary } from "@/features/marketplace/components/VideoStatusSummary";
import { ScriptReviewAction } from "@/features/marketplace/components/ScriptReviewAction";
import { localTodayYmd } from "@/features/marketplace/lib/order-deadline";
import { describeOrderLineHookProgress } from "@/features/marketplace/lib/order-line-hooks";
import { resolveOrderLineType } from "@/features/marketplace/lib/order-line-kind";
import {
  orderLineVideoKey,
  scriptHasExpandableVideos,
} from "@/features/marketplace/lib/order-line-videos";
import { ORDERS_OVERDUE_ROW_CLASS } from "@/features/marketplace/lib/orders-overdue-row";
import { PartnerVideoRow } from "@/features/marketplace/partner/components/PartnerVideoRow";
import {
  PARTNER_QUEUE_CELL_DUE_CLASS,
  PARTNER_QUEUE_CELL_RECEIVED_CLASS,
  PARTNER_QUEUE_CELL_SCRIPTS_CLASS,
  PARTNER_QUEUE_CELL_STATUS_CLASS,
  PARTNER_QUEUE_CELL_TYPE_CLASS,
  PARTNER_QUEUE_GRID_CLASS,
  PARTNER_QUEUE_ROW_PADDING_CLASS,
} from "@/features/marketplace/partner/lib/partner-queue-grid";
import {
  partnerFilledVideoSlots,
  partnerVideoSelectionKey,
  partnerWorkspaceShowsUploadTab,
} from "@/features/marketplace/partner/lib/partner-videos";
import type { OrderLineVideo } from "@/features/marketplace/types/marketplace.types";
import {
  partnerShownVideos,
  type PartnerQueueDensity,
  type PartnerWorkItem,
} from "@/features/marketplace/partner/lib/partner-work-queue";

export function partnerWorkItemDomId(item: PartnerWorkItem): string {
  return `partner-work-${item.order.id}-${item.script.id}`;
}

interface PartnerScriptRowProps {
  item: PartnerWorkItem;
  index: number;
  density: PartnerQueueDensity;
  highlighted: boolean;
  /** A Status filter is on and this line matched it. */
  matchesStatusFilter?: boolean;
  /** Opens every line and video of the order the Status filter hid. */
  onRevealStatusHidden?: () => void;
  tabIndex: 0 | -1;
  rowIndex: number;
  videosExpanded: boolean;
  onToggleVideosExpanded: () => void;
  onOpen: (item: PartnerWorkItem) => void;
  onOpenForUpload?: (item: PartnerWorkItem, video?: OrderLineVideo) => void;
  selectedVideoKey: string | null;
}

export function PartnerScriptRow({
  item,
  index,
  density,
  highlighted,
  matchesStatusFilter = false,
  onRevealStatusHidden,
  tabIndex,
  rowIndex,
  videosExpanded,
  onToggleVideosExpanded,
  onOpen,
  onOpenForUpload,
  selectedVideoKey,
}: PartnerScriptRowProps) {
  const { t } = useTranslation();
  const panelId = useId();
  const opensUploadPanel = partnerWorkspaceShowsUploadTab(item.script);
  const todayYmd = localTodayYmd();
  const fullText = [item.title, item.subtitle].filter(Boolean).join("\n\n");
  const hookProgress = describeOrderLineHookProgress(item.script);
  const showRevisionNote =
    item.script.fulfillmentStatus === "SENT_FOR_REVISION" &&
    Boolean(item.script.revisionNote?.trim());
  const expandableVideos = scriptHasExpandableVideos(item.script);
  const videos = partnerShownVideos(item);
  const legacyReview =
    !expandableVideos &&
    (item.script.scriptReviewId || item.script.preflightUrl) &&
    (item.status === "SUBMITTED_FOR_FEEDBACK" ||
      item.status === "SUBMITTED_FOR_FINAL_APPROVAL");

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.target !== event.currentTarget) return;
    if (event.key === "Enter") {
      event.preventDefault();
      onOpen(item);
    } else if (event.key === "ArrowRight" && expandableVideos && !videosExpanded) {
      event.preventDefault();
      onToggleVideosExpanded();
    } else if (event.key === "ArrowLeft" && expandableVideos && videosExpanded) {
      event.preventDefault();
      onToggleVideosExpanded();
    }
  };

  return (
    <li role="presentation">
      <div
        id={partnerWorkItemDomId(item)}
        role="row"
        aria-level={2}
        aria-rowindex={rowIndex}
        aria-expanded={expandableVideos ? videosExpanded : undefined}
        aria-controls={expandableVideos ? panelId : undefined}
        data-partner-work-item
        data-overdue={item.isOverdue ? "true" : undefined}
        data-tree-row
        tabIndex={tabIndex}
        aria-label={t("marketplace.orders.list.scriptRowLabel", {
          index,
          title: item.title,
        })}
        aria-current={highlighted ? "true" : undefined}
        onClick={() => onOpen(item)}
        onKeyDown={handleKeyDown}
        className={cn(
          PARTNER_QUEUE_GRID_CLASS,
          PARTNER_QUEUE_ROW_PADDING_CLASS,
          "group/script relative scroll-mt-20 cursor-pointer outline-none transition-colors duration-150",
          "focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring/50",
          density === "compact" ? "min-h-12 py-1.5" : "min-h-16 py-2.5",
          highlighted || matchesStatusFilter
            ? "bg-primary-subtle"
            : "bg-muted/20 hover:bg-muted/45",
          !highlighted && item.isOverdue && ORDERS_OVERDUE_ROW_CLASS,
        )}
      >
        <span
          aria-hidden
          className="absolute bottom-0 left-5 top-0 w-px bg-border"
        />
        <span
          aria-hidden
          className="absolute left-5 top-1/2 h-px w-4 bg-border"
        />

        <span role="gridcell" className="flex items-center">
          {expandableVideos ? (
            <button
              type="button"
              aria-expanded={videosExpanded}
              aria-label={t(
                videosExpanded
                  ? "marketplace.ordersDashboard.collapseVideos"
                  : "marketplace.ordersDashboard.expandVideos",
                {
                  title: item.title,
                  defaultValue: videosExpanded
                    ? "Collapse videos for {{title}}"
                    : "Expand videos for {{title}}",
                },
              )}
              className="flex size-7 -ml-1.5 cursor-pointer items-center justify-center rounded-sm text-muted-foreground/70 transition-colors hover:bg-muted hover:text-foreground"
              onClick={(event) => {
                event.stopPropagation();
                onToggleVideosExpanded();
              }}
            >
              <Icon
                name="chevron-right"
                size="sm"
                color="inherit"
                className={cn(
                  "transition-transform duration-200 ease-out motion-reduce:transition-none",
                  videosExpanded && "rotate-90",
                )}
                aria-hidden
              />
            </button>
          ) : null}
        </span>

        <div role="gridcell" className="min-w-0 pl-2">
          <Tooltip>
            <TooltipTrigger asChild>
              <div className="min-w-0">
                <p
                  className={cn(
                    "text-xs font-medium leading-4 text-foreground",
                    density === "compact" ? "line-clamp-1" : "line-clamp-2",
                  )}
                >
                  <span className="mr-1.5 tabular-nums text-muted-foreground/70">
                    {index}.
                  </span>
                  {item.title}
                </p>
                {item.subtitle ? (
                  <p
                    className={cn(
                      "mt-0.5 min-w-0 text-[11px] leading-4 text-muted-foreground",
                      density === "compact" ? "line-clamp-1" : "line-clamp-2",
                    )}
                  >
                    {item.subtitle}
                  </p>
                ) : null}
              </div>
            </TooltipTrigger>
            <TooltipContent className="max-w-md whitespace-pre-line">
              {fullText}
            </TooltipContent>
          </Tooltip>
          {showRevisionNote ? (
            <p className="mt-1 flex items-start gap-1 text-[11px] text-warning-subtle-foreground">
              <Icon
                name="message-circle"
                size="xs"
                color="inherit"
                className="mt-0.5 shrink-0"
                aria-hidden
              />
              <span className="min-w-0 line-clamp-1">
                <span className="font-medium">
                  {t("marketplace.orders.list.revisionNote")}:
                </span>{" "}
                {item.script.revisionNote}
              </span>
            </p>
          ) : null}
          <OrderScriptReferenceVideoLink url={item.script.referenceVideoUrl} />
          {hookProgress ? (
            <p className="mt-1 text-[11px] tabular-nums text-muted-foreground">
              {t("marketplace.partner.upload.hooksDelivered", {
                count: hookProgress.total,
                delivered: hookProgress.delivered,
                defaultValue_one: "{{delivered}} of {{count}} hook delivered",
                defaultValue_other: "{{delivered}} of {{count}} hooks delivered",
              })}
            </p>
          ) : null}
          <div className="mt-1 flex flex-wrap items-center gap-2 sm:hidden">
            <VideoStatusSummary script={item.script} viewer="partner" />
            <DeadlineChip
              ymd={item.dueDate}
              todayYmd={todayYmd}
              workingWeekdays={item.order.vendor.workingWeekdays}
              muted
            />
          </div>
        </div>

        <span role="gridcell" className={PARTNER_QUEUE_CELL_TYPE_CLASS}>
          <OrderTypeBadge type={resolveOrderLineType(item.script)} />
        </span>

        <span
          role="gridcell"
          aria-hidden
          className={cn(
            PARTNER_QUEUE_CELL_SCRIPTS_CLASS,
            "text-xs text-muted-foreground/45",
          )}
        >
          —
        </span>

        <span role="gridcell" className={PARTNER_QUEUE_CELL_STATUS_CLASS}>
          <VideoStatusSummary script={item.script} viewer="partner" />
        </span>

        <span
          role="gridcell"
          aria-hidden
          className={PARTNER_QUEUE_CELL_RECEIVED_CLASS}
        />

        <span role="gridcell" className={PARTNER_QUEUE_CELL_DUE_CLASS}>
          <DeadlineChip
              ymd={item.dueDate}
              todayYmd={todayYmd}
              workingWeekdays={item.order.vendor.workingWeekdays}
              muted
            />
        </span>

        <span role="gridcell" className="flex items-center justify-end">
          {legacyReview ? (
            <ScriptReviewAction
              scriptReviewId={item.script.scriptReviewId}
              preflightUrl={item.script.preflightUrl}
              label={t("marketplace.partner.actions.view", "View")}
              ariaLabel={t("marketplace.partner.actions.viewVideoReviewAria", {
                title: item.title,
                defaultValue:
                  "View video review for {{title}} (opens in a new tab)",
              })}
              variant="outline"
              size="sm"
              compact
            />
          ) : !expandableVideos &&
            (item.script.scriptReviewId || item.script.preflightUrl) ? (
            <ScriptReviewAction
              scriptReviewId={item.script.scriptReviewId}
              preflightUrl={item.script.preflightUrl}
              label={t("marketplace.partner.actions.view", "View")}
              ariaLabel={t("marketplace.partner.actions.viewVideoReviewAria", {
                title: item.title,
                defaultValue:
                  "View video review for {{title}} (opens in a new tab)",
              })}
              variant="outline"
              size="sm"
              compact
            />
          ) : (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-8 text-xs text-muted-foreground opacity-0 transition-opacity duration-150 ease-out group-hover/script:opacity-100 group-focus-within/script:opacity-100 motion-reduce:transition-none [@media(hover:none)]:opacity-100"
              onClick={(event) => {
                event.stopPropagation();
                if (opensUploadPanel && onOpenForUpload) {
                  onOpenForUpload(item);
                  return;
                }
                onOpen(item);
              }}
            >
              {t(
                item.status === "SUBMITTED_FOR_FEEDBACK" ||
                  item.status === "SUBMITTED_FOR_FINAL_APPROVAL"
                  ? "marketplace.partner.actions.viewSubmission"
                  : item.status === "DELIVERED" || item.status === "CANCELLED"
                    ? "marketplace.partner.actions.view"
                    : "marketplace.partner.actions.openUpload",
              )}
            </Button>
          )}
        </span>
      </div>

      {expandableVideos && videosExpanded ? (
        <ul id={panelId} role="presentation" className="flex flex-col">
          {videos.map((video, videoIndex) => {
            const videoKey = partnerVideoSelectionKey(video);
            return (
              <PartnerVideoRow
                key={orderLineVideoKey(video)}
                orderId={item.order.id}
                orderScriptId={item.script.id}
                scriptTitle={item.title}
                fulfillmentStatus={item.script.fulfillmentStatus}
                video={video}
                videos={partnerFilledVideoSlots(item.script)}
                rowIndex={rowIndex + videoIndex + 1}
                selected={selectedVideoKey === videoKey}
                matchesStatusFilter={Boolean(
                  item.matchedVideos?.some(
                    (matched) => orderLineVideoKey(matched) === orderLineVideoKey(video),
                  ),
                )}
                onSelect={() => {
                  if (onOpenForUpload) onOpenForUpload(item, video);
                  else onOpen(item);
                }}
              />
            );
          })}
          {item.hiddenVideoCount > 0 ? (
            <li role="presentation">
              <HiddenByFilterNote
                kind="videos"
                count={item.hiddenVideoCount}
                onReveal={onRevealStatusHidden}
              />
            </li>
          ) : null}
        </ul>
      ) : null}
    </li>
  );
}
